import LZString from 'lz-string';
import { useMemo, useState } from 'react';

import DashboardPage from '../DashboardPage';
import styles from './LzStringDecoderPage.module.css';

const formatJson = (value: unknown, pretty: boolean) =>
  JSON.stringify(value, null, pretty ? 2 : 0);

const tryDecodeURIComponent = (value: string) => {
  try {
    return { ok: true, value: decodeURIComponent(value) };
  } catch {
    return { ok: false, value };
  }
};

const tryParseJson = (value: string) => {
  try {
    return { ok: true, value: JSON.parse(value) };
  } catch {
    return { ok: false, value };
  }
};

const formatDecodedValue = (value: string, pretty: boolean) => {
  const firstParse = tryParseJson(value);
  if (firstParse.ok) {
    if (typeof firstParse.value === 'string') {
      const secondParse = tryParseJson(firstParse.value);
      if (secondParse.ok) {
        return formatJson(secondParse.value, pretty);
      }

      return formatJson(firstParse.value, pretty);
    }

    return formatJson(firstParse.value, pretty);
  }

  return value;
};

const normalizeJsonValue = (value: string) => {
  const firstParse = tryParseJson(value);
  if (!firstParse.ok) {
    return value;
  }

  if (typeof firstParse.value === 'string') {
    const secondParse = tryParseJson(firstParse.value);
    if (secondParse.ok) {
      return secondParse.value;
    }

    return firstParse.value;
  }

  return firstParse.value;
};

const jsonTokenRegex =
  /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

const getJsonTokenColor = (token: string, isDark: boolean) => {
  const trimmed = token.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed === 'true' || trimmed === 'false') {
    return isDark ? 'var(--ui-pink-300)' : 'var(--ui-pink-700)';
  }

  if (trimmed === 'null') {
    return isDark ? 'var(--ui-gray-200)' : 'var(--ui-gray-700)';
  }

  if (/^-?\d/.test(trimmed)) {
    return isDark ? 'var(--ui-yellow-300)' : 'var(--ui-yellow-700)';
  }

  if (trimmed.startsWith('"')) {
    if (trimmed.endsWith(':')) {
      return isDark ? 'var(--ui-light-blue-300)' : 'var(--ui-blue-700)';
    }

    return isDark ? 'var(--ui-green-300)' : 'var(--ui-green-700)';
  }

  return '';
};

const highlightJson = (value: string, isDark: boolean) =>
  value.split(jsonTokenRegex).map((token, index) => {
    if (!token) {
      return null;
    }

    const color = getJsonTokenColor(token, isDark);
    if (!color) {
      return token;
    }

    return (
      <span style={{ color }} key={index}>
        {token}
      </span>
    );
  });

const tryDecompress = (value: string) => {
  const direct = LZString.decompressFromEncodedURIComponent(value);
  if (direct !== null) {
    return { ok: true, value: direct };
  }

  const decoded = tryDecodeURIComponent(value);
  if (decoded.ok) {
    const decodedValue = LZString.decompressFromEncodedURIComponent(
      decoded.value
    );
    if (decodedValue !== null) {
      return { ok: true, value: decodedValue };
    }
  }

  return { ok: false, value };
};

const extractQueryValues = (value: string) => {
  const decoded = tryDecodeURIComponent(value);
  const candidate = decoded.ok ? decoded.value : value;
  const query = candidate.includes('?')
    ? candidate.split('?').slice(1).join('?')
    : candidate;

  if (!query.includes('=')) {
    return [];
  }

  const params = new URLSearchParams(query);
  const entries: Array<[string, string]> = [];
  params.forEach((paramValue, key) => {
    entries.push([key, paramValue]);
  });

  return entries;
};

const LzStringDecoderPage = () => {
  const [encoded, setEncoded] = useState('');
  const [isPretty, setIsPretty] = useState(true);
  const [isDark, setIsDark] = useState(true);

  const { decoded, error } = useMemo(() => {
    const trimmed = encoded.trim();
    if (!trimmed) {
      return {
        decoded: '',
        error: '',
      };
    }

    const queryValues = extractQueryValues(trimmed);
    if (queryValues.length > 0) {
      const decodedParams: Record<string, unknown> = {};

      queryValues.forEach(([key, value]) => {
        const decompressed = tryDecompress(value);
        if (decompressed.ok && decompressed.value !== '') {
          decodedParams[key] = normalizeJsonValue(decompressed.value);
          return;
        }

        decodedParams[key] = value;
      });

      if (Object.keys(decodedParams).length > 0) {
        const formatted = formatJson(decodedParams, isPretty);

        return {
          decoded: formatted,
          error: '',
        };
      }
    }

    const decodedValue = tryDecompress(trimmed);
    if (!decodedValue.ok) {
      return {
        decoded: '',
        error:
          'Unable to decode. Make sure this is an LZString encoded URI value.',
      };
    }

    if (!isPretty) {
      return {
        decoded: decodedValue.value,
        error: '',
      };
    }

    const formattedValue = formatDecodedValue(decodedValue.value, true);

    return {
      decoded: formattedValue,
      error: '',
    };
  }, [encoded, isPretty]);

  const outputPlaceholder = 'Decoded output will appear here';
  const outputTheme = isDark ? 'dark' : 'light';

  return (
    <DashboardPage
      title={['Deeplink Decoder']}
      backPath="/tools"
      loading={false}
      scrollY
    >
      <div className={styles.container}>
        <div className={styles.section}>
          <label className={styles.label} htmlFor="lzstring-encoded-input">
            Encoded string
          </label>
          <textarea
            id="lzstring-encoded-input"
            className={styles.textarea}
            value={encoded}
            onChange={(event) => setEncoded(event.target.value)}
            placeholder="Paste your LZString encoded URI value"
          />
        </div>
        <div className={styles.section}>
          <div className={styles.headerRow}>
            <p className={styles.label}>Decoded result</p>
            <div className={styles.toggles}>
              <label className={styles.toggleLabel} htmlFor="lzstring-pretty">
                Pretty
              </label>
              <input
                className={styles.toggle}
                type="checkbox"
                id="lzstring-pretty"
                checked={isPretty}
                onChange={(event) => setIsPretty(event.target.checked)}
              />
              <label
                className={styles.toggleLabel}
                htmlFor="lzstring-dark-mode"
              >
                Dark
              </label>
              <input
                className={styles.toggle}
                type="checkbox"
                id="lzstring-dark-mode"
                checked={isDark}
                onChange={(event) => setIsDark(event.target.checked)}
              />
            </div>
          </div>
          {isPretty ? (
            <pre
              className={styles.outputPre}
              data-theme={outputTheme}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: migrated from develop
              tabIndex={0}
              aria-label="Decoded result"
            >
              {decoded ? (
                highlightJson(decoded, isDark)
              ) : (
                <span className={styles.placeholder}>{outputPlaceholder}</span>
              )}
            </pre>
          ) : (
            <textarea
              className={styles.textarea}
              data-theme={outputTheme}
              value={decoded}
              readOnly
              placeholder={outputPlaceholder}
              aria-label="Decoded result"
            />
          )}
          {error && <p className={styles.error}>{error}</p>}
        </div>
      </div>
    </DashboardPage>
  );
};

export default LzStringDecoderPage;
